import { z } from "zod"
import { SalesChannel, SalesSkuSchema } from "./SalesCommons"

export enum ProductCartMetadataFields {
	SerialNumbers = "serial_numbers",
}

export const BaseSalesProductModelSchema = z.object({
	/**
	 * ID of the bundle, variant or parent product
	 */
	id: z.string().min(1).describe("Unique identifier"),

	/**
	 * Name of the bundle, variant or parent product.
	 */
	name: z.string().min(1).describe("Product name"),

	/**
	 * Price of the bundle, variant or parent product
	 */
	price: z.number().min(0).nullable(),

	/**
	 * Price of the bundle, variant or parent product
	 *
	 * Populated when the following conditions are met
	 * 1. A base price is defined
	 * 2. A sale price is defined
	 * 3. The sales price is lower than the base price
	 * 4. The sales price is active
	 */
	sale_price: z.number().min(0).nullable(),

	/**
	 * Categories of the bundle, variant or parent product
	 */
	categories: z.array(z.string()).describe("Product categories"),

	/**
	 * SKU of the bundle, variant or parent product
	 */
	sku: SalesSkuSchema.nullable(),

	/**
	 * Which sales channels should the bundle, variant or parent product be exposed to.
	 */
	sales_channels: z.array(
		z.enum([SalesChannel.FieldSales, SalesChannel.SelfService, SalesChannel.TeleSales, SalesChannel.IkStores]),
	),

	/**
	 * Bundle (not bundle's items), variant or parent product shipment override.
	 * Any shipment-relate coupons voids this functionality.
	 */
	shipping_cost: z.number().min(0).nullable(),

	/**
	 * Images of the bundle, variant or parent product
	 */
	images: z.array(z.string()),

	/**
	 * Certain flows/channels will require certain fields to be captured. For example, field sales + bootstock will require device serial numbers to be captured.
	 * Each product will have a list of possible fields to be recorded when added to a cart.
	 *
	 * For example, a card machine's list will have the field serial_numbers. Any system creating a cart will know what extra fields to populate based on the
	 * product's extra fields and in this case, the caller will know to populate the field serial_numbers.
	 */
	cart_metadata_fields: z.array(z.enum([ProductCartMetadataFields.SerialNumbers])),
})

export type BaseSalesProductModel = z.infer<typeof BaseSalesProductModelSchema>

const SalesProductVariantModelSchema = BaseSalesProductModelSchema.extend({
	/**
	 * This field helps when a variant's name should include the parent's.
	 */
	parent_name: z.string().min(1),

	parent_id: z.string().min(1),
})
export type SalesProductVariantModel = z.infer<typeof SalesProductVariantModelSchema>

const SalesProductBundleItemModelSchema = BaseSalesProductModelSchema.pick({
	name: true,
	sku: true,
	images: true,
	cart_metadata_fields: true,
}).extend({
	// If this bundle item is a variant, it will have the parent's name
	parent_name: SalesProductVariantModelSchema.shape.parent_name.nullable(),
	quantity: z.number().min(1),
	original_price: z.number().min(0),
})
export type SalesProductBundleItemModel = z.infer<typeof SalesProductBundleItemModelSchema>

export const SalesProductBundleDetailsSchema = z.object({
	bundle_items: z.array(SalesProductBundleItemModelSchema),
	/**
	 * Each item in a bundle has an original price (NOT the sale price).
	 * The `savings` field indicates how much is saved
	 * on all the items.
	 */
	savings: z.number().min(0),
})
export type SalesProductBundleDetails = z.infer<typeof SalesProductBundleDetailsSchema>

/**
 * S&O sales product entity schema
 */
export const SalesProductModelSchema = BaseSalesProductModelSchema.extend({
	/**
	 * If variants has items, it implies the product is a parent product.
	 * TAKE NOTE: A product with variants CANNOT be a bundle.
	 *
	 * This means, if `variants` has items, bundle_details will be null.
	 */
	variants: z.array(SalesProductVariantModelSchema),
	/**
	 * If this is populated, it means the product is a bundle.
	 * TAKE NOTE: A bundle CANNOT have variants.
	 *
	 * This means, if `bundle_details` is populated, variants will be empty.
	 */
	bundle_details: SalesProductBundleDetailsSchema.nullable(),
}).refine((data) => data.variants.length === 0 || data.bundle_details === null, {
	message: "A product cannot have both variants and bundle_details populated.",
	path: ["variants", "bundle_details"],
})
export type SalesProductModel = z.infer<typeof SalesProductModelSchema>
